// ** React Imports
import { useState, useEffect, Fragment } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** MUI Imports
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Fade from '@mui/material/Fade'
import List from '@mui/material/List'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import ListItemIcon from '@mui/material/ListItemIcon'
import { styled, useTheme } from '@mui/material/styles'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MuiListItem from '@mui/material/ListItem'

// ** Third Party Imports
import clsx from 'clsx'
import { usePopper } from 'react-popper'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Theme Config Import
import themeConfig from 'src/configs/themeConfig'

// ** Custom Components Imports
import UserIcon from 'src/layouts/components/UserIcon'
import Translations from 'src/layouts/components/Translations'
import CanViewNavGroup from 'src/layouts/components/acl/CanViewNavGroup'

// ** Utils
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { hasActiveChild } from 'src/@core/layouts/utils'

import HorizontalNavItems from './HorizontalNavItems'

// ** Styled Components
const ListItem = styled(MuiListItem)(({ theme }) => ({
  cursor: 'pointer',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1)
}))

const NavigationMenu = styled(Paper)(({ theme }) => ({
  overflowY: 'auto',
  padding: theme.spacing(2, 0),
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  ...(themeConfig.menuTextTruncate ? { width: 320 } : { minWidth: 320 }),
  '&::-webkit-scrollbar': {
    width: 6
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 20,
    background: hexToRGBA(theme.palette.mode === 'light' ? '#BFBFD5' : '#57596C', 0.6)
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: 20,
    background: 'transparent'
  },
  '& .MuiList-root': {
    paddingTop: 0,
    paddingBottom: 0
  },
  '& .menu-group.Mui-selected': {
    borderRadius: 0,
    backgroundColor: theme.palette.action.hover
  }
}))

const HorizontalNavGroup = props => {
  // ** Props
  const { item, hasParent, settings } = props

  // ** Hooks & Vars
  const theme = useTheme()

  const router = useRouter()

  const currentURL = router.asPath

  const { skin, direction } = settings

  const { navSubItemIcon, menuTextTruncate, horizontalMenuToggle, horizontalMenuAnimation } = themeConfig

  const popperOffsetHorizontal = direction === 'rtl' ? 16 : -16

  const popperPlacement = direction === 'rtl' ? 'bottom-end' : 'bottom-start'

  const popperPlacementSubMenu = direction === 'rtl' ? 'left-start' : 'right-start'

  // ** States
  const [menuOpen, setMenuOpen] = useState(false)

  const [popperElement, setPopperElement] = useState(null)

  const [anchorEl, setAnchorEl] = useState(null)

  const [referenceElement, setReferenceElement] = useState(null)

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement: hasParent ? popperPlacementSubMenu : popperPlacement,
    modifiers: [
      {
        enabled: true,
        name: 'offset',
        options: {
          offset: hasParent ? [-8, 15] : [popperOffsetHorizontal, -7]
        }
      },
      {
        enabled: true,
        name: 'flip'
      }
    ]
  })

  const handleGroupOpen = event => {
    setAnchorEl(event.currentTarget)
    setMenuOpen(true)
    update ? update() : null
  }

  const handleGroupClose = () => {
    setAnchorEl(null)
    setMenuOpen(false)
  }

  const handleMenuToggleOnClick = event => {
    if (anchorEl) {
      handleGroupClose()
    } else {
      handleGroupOpen(event)
    }
  }

  useEffect(() => {
    handleGroupClose()

  }, [router.asPath])
  const icon = item.icon ? item.icon : navSubItemIcon

  const toggleIcon = direction === 'rtl' ? 'mdi:chevron-left' : 'mdi:chevron-right'

  const WrapperCondition = horizontalMenuToggle === 'click'

  const MainWrapper = WrapperCondition ? ClickAwayListener : 'div'

  const ChildWrapper = WrapperCondition ? 'div' : Fragment

  const AnimationWrapper = horizontalMenuAnimation ? Fade : Fragment

  const childMenuGroupStyles = () => {
    if (attributes && attributes.popper) {
      if (direction === 'ltr') {
        if (attributes.popper['data-popper-placement'] === 'right-start') {
          return 'left'
        }
        if (attributes.popper['data-popper-placement'] === 'left-start') {
          return 'right'
        }
      } else {
        if (attributes.popper['data-popper-placement'] === 'right-start') {
          return 'right'
        }
        if (attributes.popper['data-popper-placement'] === 'left-start') {
          return 'left'
        }
      }
    }
  }

  return (
    <CanViewNavGroup navGroup={item}>
      {/* @ts-ignore */}
      <MainWrapper {...(WrapperCondition ? { onClickAway: handleGroupClose } : { onMouseLeave: handleGroupClose })}>
        <ChildWrapper>
          <List className="custom-nav-menu-list" component='div' sx={{ py: skin === 'bordered' ? 0.5 : 0.75 }}>
            <ListItem
              aria-haspopup='true'
              {...(WrapperCondition ? {} : { onMouseEnter: handleGroupOpen })}
              className={clsx('menu-group', { 'Mui-selected': hasActiveChild(item, currentURL) })}
              {...(horizontalMenuToggle === 'click' ? { onClick: handleMenuToggleOnClick } : {})}
              sx={{
                ...(menuOpen ? { backgroundColor: '#b4b8d5' } : {}),
                ...(!hasParent
                  ? {
                      borderRadius: '20px',
                      transition: 'none',
                      '&:hover': {
                        backgroundColor: '#b4b8d5 !important',
                        transition: 'none',
                        '& .MuiTypography-root, & .MuiListItemIcon-root, & svg': {
                          color: '#12205A !important'
                        }
                      },
                      '&:hover .MuiTypography-root, &:hover .MuiListItemIcon-root, &:hover svg': {
                        color: '#12205A !important'
                      },
                    '&.Mui-selected': {
                     // backgroundColor: '#250303',
                        backgroundColor: '#b4b8d5',
                        '& .MuiTypography-root, & .MuiListItemIcon-root, & svg': {
                          color: '#12205A'
                        }
                      }
                    }
                  : {})
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2
                }}
                ref={setReferenceElement}
              >

                <Box sx={{ width: '20%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 'auto', color: 'customColors.headerText' }}>
                    <UserIcon icon={icon} fontSize={icon === navSubItemIcon ? '0.5rem' : '1.875rem'} />
                  </ListItemIcon>
                </Box>
                <Box
                  sx={{
                    width: '80%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    ...(menuTextTruncate && { overflow: 'hidden' })
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography
                      {...(menuTextTruncate && { noWrap: true })}
                      sx={{
                        textTransform: 'uppercase',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'customColors.headerText',
                        letterSpacing: '0.5px'
                      }}
                    >
                      <Translations text={item.title} />
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'customColors.headerText' }}>
                      {item.badgeContent ? (
                        <Chip
                          size='small'
                          label={item.badgeContent}
                          color={item.badgeColor || 'primary'}
                          sx={{ mr: 1, '& .MuiChip-label': { px: 2, lineHeight: 1.385, textTransform: 'capitalize' } }}
                        />
                      ) : null}
                      <Icon icon={hasParent ? toggleIcon : 'mdi:chevron-down'} fontSize='1.25rem' />
                    </Box>
                  </Box>
                  {item.subtitle && (
                    <Typography
                      variant='caption'
                      sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        mt: 0.5,
                        lineHeight: 1.2,
                        ...(menuTextTruncate && { noWrap: true })
                      }}
                    >
                      {item.subtitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            </ListItem>
            <AnimationWrapper {...(horizontalMenuAnimation && { in: menuOpen, timeout: { exit: 300, enter: 400 } })}>
              <Box
                style={styles.popper}
                ref={setPopperElement}
                {...attributes.popper}
                sx={{
                  // zIndex: theme.zIndex.appBar,
                  zIndex: 2000,
                  ...(!horizontalMenuAnimation && { display: menuOpen ? 'block' : 'none' }),
                  pl: childMenuGroupStyles() === 'left' ? (skin === 'bordered' ? 1.5 : 1.25) : 0,
                  pr: childMenuGroupStyles() === 'right' ? (skin === 'bordered' ? 1.5 : 1.25) : 0,
                  ...(hasParent ? { position: 'fixed !important' } : { pt: skin === 'bordered' ? 5.25 : 5.5 })
                }}
              >
                <NavigationMenu
                  sx={{
                    ...(hasParent
                      ? { overflowX: 'visible', maxHeight: 'calc(100vh - 21rem)' }
                      : { maxHeight: 'calc(100vh - 13rem)' }),
                    ...(skin === 'bordered'
                      ? { boxShadow: 0, border: `1px solid ${theme.palette.divider}`, borderRadius: '20px' }
                      : { boxShadow: 4, borderRadius: '20px' })
                  }}
                >
                  <HorizontalNavItems {...props} hasParent horizontalNavItems={item.children} />
                </NavigationMenu>
              </Box>
            </AnimationWrapper>
          </List>
        </ChildWrapper>
      </MainWrapper>
    </CanViewNavGroup>
  )
}

export default HorizontalNavGroup
